import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { processIntake, createIntakeEvent, getThreadsSimple, transcribeAudio } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Upload, FileText, CheckCircle, Loader2, Mic, AlertCircle, MicOff, Square, MessageSquare, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SUPPORTED_AUDIO_FORMATS = ['.mp3', '.m4a', '.wav', '.mp4', '.mpeg', '.mpga', '.webm'];

// Check if mobile device
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window.innerWidth < 768;
};

// Check Web Speech API support
const isWebSpeechSupported = () => {
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

const IntakePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isMobile] = useState(isMobileDevice());
  const [activeTab, setActiveTab] = useState(isMobile ? 'voice' : 'text');
  
  // Text form state
  const [formData, setFormData] = useState({
    transcript: '',
    participants: '',
    channel: 'Meet',
    datetime: new Date().toISOString().slice(0, 16)
  });
  
  // Audio upload state
  const [audioFile, setAudioFile] = useState(null);
  const [audioFormData, setAudioFormData] = useState({
    participants: '',
    channel: 'Phone',
    datetime: new Date().toISOString().slice(0, 16)
  });
  
  // Voice Chat state
  const [voiceState, setVoiceState] = useState('idle'); // idle, listening, processing, transcribed
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceProvider, setVoiceProvider] = useState(null); // 'web_speech' or 'whisper'
  const [voiceConfidence, setVoiceConfidence] = useState(null);
  const [voiceLanguage, setVoiceLanguage] = useState(null);
  const [useWhisperToggle, setUseWhisperToggle] = useState(false);
  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState('');
  const [newThreadName, setNewThreadName] = useState('');
  
  // Refs for audio recording
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  // Load threads for Voice Chat
  useEffect(() => {
    const loadThreads = async () => {
      try {
        const response = await getThreadsSimple();
        setThreads(response.data.filter(t => t.status === 'Active'));
      } catch (error) {
        console.error('Failed to load threads:', error);
      }
    };
    loadThreads();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const stopAllAudio = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Text form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    try {
      const response = await processIntake({
        transcript: formData.transcript,
        participants: formData.participants.split(',').map(p => p.trim()).filter(Boolean),
        channel: formData.channel,
        datetime: new Date(formData.datetime).toISOString()
      });
      setResult(response.data);
      toast.success('Transcript processed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process intake');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData({ ...formData, transcript: event.target?.result });
      };
      reader.readAsText(file);
    }
  };

  const handleAudioFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!SUPPORTED_AUDIO_FORMATS.includes(ext)) {
        toast.error(`Unsupported format. Supported: ${SUPPORTED_AUDIO_FORMATS.join(', ')}`);
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 25 MB');
        return;
      }
      setAudioFile(file);
    }
  };

  const handleAudioSubmit = async (e) => {
    e.preventDefault();
    if (!audioFile) {
      toast.error('Please select an audio file');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', audioFile);
      formDataToSend.append('channel', audioFormData.channel);
      formDataToSend.append('participants', audioFormData.participants);

      const response = await axios.post(`${API_URL}/intake/upload-audio`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResult(response.data);
      toast.success('Audio transcribed and processed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process audio');
    } finally {
      setLoading(false);
    }
  };

  // Voice Chat - Start listening
  const startVoiceCapture = async () => {
    setVoiceTranscript('');
    setVoiceProvider(null);
    setVoiceConfidence(null);
    setVoiceLanguage(null);
    
    // If user toggled Whisper, go straight to Whisper
    if (useWhisperToggle) {
      await startWhisperRecording();
      return;
    }
    
    // Try Web Speech API first
    if (isWebSpeechSupported()) {
      try {
        await startWebSpeechRecognition();
      } catch (error) {
        console.warn('Web Speech failed, falling back to Whisper:', error);
        toast.info('Switching to Whisper for better accuracy');
        await startWhisperRecording();
      }
    } else {
      // Fallback to Whisper if Web Speech not supported
      toast.info('Using Whisper transcription (Web Speech not available)');
      await startWhisperRecording();
    }
  };

  // Web Speech API
  const startWebSpeechRecognition = () => {
    return new Promise((resolve, reject) => {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US'; // Default, will detect
      
      let finalTranscript = '';
      let lastConfidence = 0;
      
      recognition.onstart = () => {
        setVoiceState('listening');
        setVoiceProvider('web_speech');
        resolve();
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'not-allowed' || event.error === 'no-speech') {
          setVoiceState('idle');
          reject(new Error(event.error));
        }
        // For other errors, try Whisper fallback
        if (event.error === 'network' || event.error === 'aborted') {
          stopAllAudio();
          startWhisperRecording();
        }
      };
      
      recognition.onresult = (event) => {
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript + ' ';
            lastConfidence = result[0].confidence;
          } else {
            interimTranscript += result[0].transcript;
          }
        }
        
        setVoiceTranscript(finalTranscript + interimTranscript);
        setVoiceConfidence(lastConfidence);
        
        // If confidence is too low, suggest Whisper
        if (lastConfidence > 0 && lastConfidence < 0.7) {
          setVoiceLanguage('low_confidence');
        }
      };
      
      recognition.onend = () => {
        if (voiceState === 'listening') {
          setVoiceState('transcribed');
          setVoiceTranscript(finalTranscript.trim());
          
          // Auto-fallback to Whisper if confidence is low
          if (lastConfidence > 0 && lastConfidence < 0.5 && finalTranscript.trim()) {
            toast.info('Low confidence detected. Consider using Whisper for better accuracy.');
          }
        }
      };
      
      recognitionRef.current = recognition;
      recognition.start();
    });
  };

  // Whisper Recording (fallback)
  const startWhisperRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeWithWhisper(audioBlob);
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect data every second
      
      setVoiceState('listening');
      setVoiceProvider('whisper');
      
    } catch (error) {
      console.error('Microphone access denied:', error);
      toast.error('Microphone access denied. Please allow microphone access.');
      setVoiceState('idle');
    }
  };

  // Stop voice capture
  const stopVoiceCapture = () => {
    if (voiceProvider === 'web_speech' && recognitionRef.current) {
      recognitionRef.current.stop();
      setVoiceState('transcribed');
    } else if (voiceProvider === 'whisper' && mediaRecorderRef.current) {
      setVoiceState('processing');
      mediaRecorderRef.current.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  // Transcribe with Whisper
  const transcribeWithWhisper = async (audioBlob) => {
    setVoiceState('processing');
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const response = await axios.post(`${API_URL}/intake/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setVoiceTranscript(response.data.transcript);
      setVoiceLanguage(response.data.detected_language);
      setVoiceProvider('whisper');
      setVoiceState('transcribed');
    } catch (error) {
      console.error('Whisper transcription failed:', error);
      toast.error(error.response?.data?.detail || 'Transcription failed');
      setVoiceState('idle');
    }
  };

  // Create IntakeEvent from voice (proposal only)
  const handleVoiceIntake = async () => {
    if (!voiceTranscript.trim()) {
      toast.error('No transcript to submit');
      return;
    }
    
    setLoading(true);
    
    try {
      const intakeData = {
        raw_content: voiceTranscript,
        source: 'voice',
        provider: voiceProvider,
        language: voiceLanguage,
        proposed_thread_ids: selectedThreadId && selectedThreadId !== '__new__' ? [selectedThreadId] : [],
        proposed_participants: ['Founder']
      };
      
      const response = await createIntakeEvent(intakeData);
      
      toast.success('Voice intake captured! Review required before creating evidence.');
      
      // Reset voice state
      setVoiceTranscript('');
      setVoiceState('idle');
      setSelectedThreadId('');
      setNewThreadName('');
      
      // Show result as pending review
      setResult({
        intake_event_id: response.data.id,
        status: 'pending_review',
        requires_confirmation: true,
        message: response.data.message
      });
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create intake event');
    } finally {
      setLoading(false);
    }
  };

  // Re-record with Whisper
  const retryWithWhisper = () => {
    setVoiceTranscript('');
    setVoiceState('idle');
    setUseWhisperToggle(true);
    toast.info('Click the microphone to record with Whisper');
  };

  const resetForm = () => {
    setResult(null);
    setFormData({
      transcript: '',
      participants: '',
      channel: 'Meet',
      datetime: new Date().toISOString().slice(0, 16)
    });
    setAudioFile(null);
    setAudioFormData({
      participants: '',
      channel: 'Phone',
      datetime: new Date().toISOString().slice(0, 16)
    });
    setVoiceTranscript('');
    setVoiceState('idle');
    setSelectedThreadId('');
    setNewThreadName('');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 px-4 sm:px-0" data-testid="intake-page">
      {/* Header */}
      <div>
        <h1 className="font-manrope text-xl font-bold text-zinc-900">Intake</h1>
        <p className="text-sm text-zinc-500 font-inter">
          Capture conversations via text, audio upload, or voice
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Intake Form */}
        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-manrope text-base">New Intake</CardTitle>
            <CardDescription className="font-inter text-sm">
              Text, audio file, or live voice
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="text" className="flex items-center gap-1 text-xs sm:text-sm">
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4" /> Text
                </TabsTrigger>
                <TabsTrigger value="audio" className="flex items-center gap-1 text-xs sm:text-sm">
                  <Upload className="w-3 h-3 sm:w-4 sm:h-4" /> Audio
                </TabsTrigger>
                <TabsTrigger value="voice" className="flex items-center gap-1 text-xs sm:text-sm">
                  <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" /> Voice
                </TabsTrigger>
              </TabsList>

              {/* Text Transcript Tab */}
              <TabsContent value="text">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="font-inter text-sm">Upload .txt (optional)</Label>
                    <div className="mt-1">
                      <label className="flex items-center justify-center border-2 border-dashed border-zinc-200 rounded-sm p-3 cursor-pointer hover:border-zinc-400 transition-colors">
                        <input
                          type="file"
                          accept=".txt"
                          className="hidden"
                          onChange={handleFileUpload}
                          data-testid="file-upload-input"
                        />
                        <div className="text-center">
                          <Upload className="w-5 h-5 text-zinc-400 mx-auto mb-1" />
                          <span className="text-xs text-zinc-500 font-inter">Click to upload</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label className="font-inter text-sm">Transcript</Label>
                    <Textarea
                      value={formData.transcript}
                      onChange={(e) => setFormData({ ...formData, transcript: e.target.value })}
                      className="rounded-sm h-32 font-mono text-sm"
                      placeholder="Paste conversation..."
                      required
                      data-testid="transcript-input"
                    />
                  </div>

                  <div>
                    <Label className="font-inter text-sm">Participants (comma-separated)</Label>
                    <Input
                      value={formData.participants}
                      onChange={(e) => setFormData({ ...formData, participants: e.target.value })}
                      className="rounded-sm"
                      placeholder="John, Jane"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-inter text-sm">Channel</Label>
                      <Select 
                        value={formData.channel} 
                        onValueChange={(v) => setFormData({ ...formData, channel: v })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Meet">Google Meet</SelectItem>
                          <SelectItem value="Zoom">Zoom</SelectItem>
                          <SelectItem value="Phone">Phone</SelectItem>
                          <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                          <SelectItem value="In-person">In-person</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-inter text-sm">Date & Time</Label>
                      <Input
                        type="datetime-local"
                        value={formData.datetime}
                        onChange={(e) => setFormData({ ...formData, datetime: e.target.value })}
                        className="rounded-sm"
                        required
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full rounded-sm" disabled={loading}>
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><FileText className="w-4 h-4 mr-2" /> Process Transcript</>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Audio Upload Tab */}
              <TabsContent value="audio">
                <form onSubmit={handleAudioSubmit} className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-700 font-inter">
                      <p className="font-medium">Audio File Upload</p>
                      <p>Upload recordings for Whisper transcription. Supported: .mp3, .m4a, .wav (max 25MB)</p>
                    </div>
                  </div>

                  <div>
                    <Label className="font-inter text-sm">Audio File</Label>
                    <div className="mt-1">
                      <label className="flex items-center justify-center border-2 border-dashed border-zinc-200 rounded-sm p-4 cursor-pointer hover:border-zinc-400 transition-colors">
                        <input
                          type="file"
                          accept=".mp3,.m4a,.wav,.mp4,.mpeg,.mpga,.webm"
                          className="hidden"
                          onChange={handleAudioFileSelect}
                        />
                        <div className="text-center">
                          <Mic className="w-6 h-6 text-zinc-400 mx-auto mb-1" />
                          <span className="text-sm text-zinc-500 font-inter">
                            {audioFile ? audioFile.name : 'Click to upload'}
                          </span>
                          {audioFile && (
                            <p className="text-xs text-zinc-400 mt-1">
                              {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label className="font-inter text-sm">Participants</Label>
                    <Input
                      value={audioFormData.participants}
                      onChange={(e) => setAudioFormData({ ...audioFormData, participants: e.target.value })}
                      className="rounded-sm"
                      placeholder="Founder, Partner"
                    />
                  </div>

                  <div>
                    <Label className="font-inter text-sm">Channel</Label>
                    <Select 
                      value={audioFormData.channel} 
                      onValueChange={(v) => setAudioFormData({ ...audioFormData, channel: v })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Phone">Phone Call</SelectItem>
                        <SelectItem value="In-person">In-person</SelectItem>
                        <SelectItem value="Meet">Google Meet</SelectItem>
                        <SelectItem value="Zoom">Zoom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button type="submit" className="w-full rounded-sm" disabled={loading || !audioFile}>
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transcribing...</>
                    ) : (
                      <><Mic className="w-4 h-4 mr-2" /> Transcribe & Process</>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Voice Chat Tab */}
              <TabsContent value="voice">
                <div className="space-y-4">
                  {/* Voice Chat Info */}
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-2">
                    <Volume2 className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-800 font-inter">
                      <p className="font-medium">Voice-First Intake</p>
                      <p>Speak to capture notes. Creates a proposal for your review - no auto-execution.</p>
                    </div>
                  </div>

                  {/* Whisper Toggle */}
                  <div className="flex items-center justify-between bg-zinc-50 rounded-sm p-3">
                    <div className="text-sm font-inter">
                      <span className="text-zinc-700">Use Whisper</span>
                      <span className="text-xs text-zinc-500 ml-2">(more accurate)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setUseWhisperToggle(!useWhisperToggle)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        useWhisperToggle ? 'bg-emerald-500' : 'bg-zinc-300'
                      }`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                        useWhisperToggle ? 'translate-x-5' : ''
                      }`} />
                    </button>
                  </div>

                  {/* Microphone Button */}
                  <div className="flex flex-col items-center py-6">
                    {voiceState === 'idle' && (
                      <button
                        onClick={startVoiceCapture}
                        className="w-20 h-20 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center shadow-lg transition-all hover:scale-105"
                        data-testid="voice-start-btn"
                      >
                        <Mic className="w-8 h-8" />
                      </button>
                    )}
                    
                    {voiceState === 'listening' && (
                      <button
                        onClick={stopVoiceCapture}
                        className="w-20 h-20 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg animate-pulse"
                        data-testid="voice-stop-btn"
                      >
                        <Square className="w-8 h-8" />
                      </button>
                    )}
                    
                    {voiceState === 'processing' && (
                      <div className="w-20 h-20 rounded-full bg-zinc-200 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
                      </div>
                    )}
                    
                    {voiceState === 'transcribed' && (
                      <button
                        onClick={() => { setVoiceState('idle'); setVoiceTranscript(''); }}
                        className="w-20 h-20 rounded-full bg-zinc-200 hover:bg-zinc-300 text-zinc-600 flex items-center justify-center"
                      >
                        <MicOff className="w-8 h-8" />
                      </button>
                    )}
                    
                    <p className="text-sm text-zinc-500 mt-3 font-inter">
                      {voiceState === 'idle' && 'Tap to speak'}
                      {voiceState === 'listening' && (
                        <span className="text-red-500">Recording... tap to stop</span>
                      )}
                      {voiceState === 'processing' && 'Transcribing...'}
                      {voiceState === 'transcribed' && 'Tap to re-record'}
                    </p>
                    
                    {voiceProvider && (
                      <p className="text-xs text-zinc-400 mt-1">
                        via {voiceProvider === 'web_speech' ? 'Web Speech' : 'Whisper'}
                        {voiceConfidence && ` (${Math.round(voiceConfidence * 100)}% confidence)`}
                      </p>
                    )}
                  </div>

                  {/* Transcript Preview */}
                  {voiceTranscript && (
                    <div className="space-y-4">
                      <div>
                        <Label className="font-inter text-sm">Transcript (editable)</Label>
                        <Textarea
                          value={voiceTranscript}
                          onChange={(e) => setVoiceTranscript(e.target.value)}
                          className="rounded-sm h-24 font-mono text-sm"
                          placeholder="Your voice will appear here..."
                        />
                      </div>
                      
                      {/* Low confidence warning */}
                      {voiceProvider === 'web_speech' && voiceConfidence && voiceConfidence < 0.7 && (
                        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-sm p-2">
                          <span className="text-xs text-amber-700">Low confidence. Try Whisper?</span>
                          <Button variant="outline" size="sm" onClick={retryWithWhisper}>
                            Re-record with Whisper
                          </Button>
                        </div>
                      )}

                      {/* Thread Selection */}
                      <div>
                        <Label className="font-inter text-sm">Link to Thread (optional)</Label>
                        <Select 
                          value={selectedThreadId} 
                          onValueChange={setSelectedThreadId}
                        >
                          <SelectTrigger className="rounded-sm">
                            <SelectValue placeholder="Select existing thread..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__new__">New thread (auto-named)</SelectItem>
                            {threads.map(t => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Submit as Intake Event (Proposal Only) */}
                      <Button 
                        onClick={handleVoiceIntake} 
                        className="w-full rounded-sm"
                        disabled={loading || !voiceTranscript.trim()}
                      >
                        {loading ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                        ) : (
                          <><MessageSquare className="w-4 h-4 mr-2" /> Create Intake (For Review)</>
                        )}
                      </Button>
                      
                      <p className="text-xs text-zinc-500 text-center font-inter">
                        This creates a proposal. Human confirmation required before evidence record.
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className={`border shadow-none rounded-sm ${result ? 'border-emerald-300 bg-emerald-50/50' : 'border-zinc-200'}`}>
          <CardHeader>
            <CardTitle className="font-manrope text-base flex items-center gap-2">
              {result ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : null}
              Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!result ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-zinc-200 mx-auto mb-2" />
                <p className="text-sm text-zinc-500 font-inter">
                  Submit intake to see results
                </p>
              </div>
            ) : result.requires_confirmation ? (
              // Voice Intake Result (Pending Review)
              <div className="space-y-4" data-testid="intake-pending-result">
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-center">
                  <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-amber-800">Intake Captured</p>
                  <p className="text-xs text-amber-600 mt-1">
                    Review required before creating evidence record
                  </p>
                </div>
                
                <div className="bg-white border border-zinc-200 rounded-sm p-3">
                  <p className="text-xs text-zinc-500 mb-1">Intake Event ID</p>
                  <p className="font-mono text-sm">{result.intake_event_id}</p>
                </div>
                
                <p className="text-xs text-zinc-500 text-center">
                  Navigate to Pending Intakes to confirm or reject
                </p>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1 rounded-sm"
                    onClick={() => navigate('/intake/pending')}
                  >
                    View Pending
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 rounded-sm"
                    onClick={resetForm}
                  >
                    New Intake
                  </Button>
                </div>
              </div>
            ) : (
              // Direct Intake Result (Processed)
              <div className="space-y-4" data-testid="intake-results">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white border border-zinc-200 rounded-sm p-3">
                    <p className="text-2xl font-bold font-mono text-zinc-900">{result.action_items_created}</p>
                    <p className="text-xs text-zinc-500 font-inter">Actions</p>
                  </div>
                  <div className="bg-white border border-zinc-200 rounded-sm p-3">
                    <p className="text-2xl font-bold font-mono text-zinc-900">{result.draft_messages_created}</p>
                    <p className="text-xs text-zinc-500 font-inter">Drafts</p>
                  </div>
                  <div className="bg-white border border-zinc-200 rounded-sm p-3">
                    <p className="text-2xl font-bold font-mono text-zinc-900">{result.funnel_items_updated}</p>
                    <p className="text-xs text-zinc-500 font-inter">Funnels</p>
                  </div>
                  <div className="bg-white border border-zinc-200 rounded-sm p-3">
                    <p className="text-2xl font-bold font-mono text-emerald-600">
                      {result.thread_reused ? 'Reused' : 'New'}
                    </p>
                    <p className="text-xs text-zinc-500 font-inter">Thread</p>
                  </div>
                </div>

                {result.classification && (
                  <div className="bg-white border border-zinc-200 rounded-sm p-3">
                    <p className="text-xs text-zinc-500 font-inter mb-2">Classification</p>
                    <div className="space-y-1 text-sm font-inter">
                      <p><span className="text-zinc-500">Entity:</span> {result.classification.operating_entity_name || 'Not identified'}</p>
                      <p><span className="text-zinc-500">Relationship:</span> {result.classification.relationship_type}</p>
                      <p><span className="text-zinc-500">External:</span> {result.classification.is_external ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1 rounded-sm"
                    onClick={() => navigate(`/threads/${result.thread_id}`)}
                  >
                    View Thread
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 rounded-sm"
                    onClick={resetForm}
                  >
                    New Intake
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntakePage;
